<template>
  <div class="image-news-page">
    <header class="page-header">
      <div class="container">
        <div class="header-row">
          <div class="header-left">
            <h2 class="current-category-title">{{ currentCategoryName }}</h2>
            <nav class="breadcrumb">
              <a href="/">首页</a>              
              <span>> {{ currentCategoryName }}</span>
            </nav>
          </div>
        </div>
      </div>
    </header>

    <div class="container">
      <aside class="sidebar" v-if="!searchKeyword">
        <div class="sidebar-section">
          <h3>文章分类</h3>
          <ul class="category-list">
            <li v-for="cat in categories" :key="cat.id" class="tree-item">
              <div class="tree-row">
                <span v-if="cat.children && cat.children.length > 0" 
                      class="expand-icon" 
                      @click.prevent="toggleExpand(cat.id)">
                  {{ expandedCats.includes(cat.id) ? '▼' : '▶' }}
                </span>
                <span v-else class="expand-icon empty"></span>
                <a href="#" @click.prevent="filterByCategory(cat.id)" 
                   :class="{ active: activeCategoryId === cat.id }">
                  {{ cat.name }} ({{ cat.count }})
                </a>
              </div>
              <ul v-if="cat.children && cat.children.length > 0 && expandedCats.includes(cat.id)" class="sub-category">
                <li v-for="sub1 in cat.children" :key="sub1.id" class="tree-item">
                  <div class="tree-row">
                    <span v-if="sub1.children && sub1.children.length > 0" 
                          class="expand-icon" 
                          @click.prevent="toggleExpand(sub1.id)">
                      {{ expandedCats.includes(sub1.id) ? '▼' : '▶' }}
                    </span>
                    <span v-else class="expand-icon empty"></span>
                    <a href="#" @click.prevent="filterByCategory(sub1.id)" 
                       :class="{ active: activeCategoryId === sub1.id }">
                      {{ sub1.name }} ({{ sub1.count }})
                    </a>
                  </div>
                  <ul v-if="sub1.children && sub1.children.length > 0 && expandedCats.includes(sub1.id)" class="sub-category">
                    <li v-for="sub2 in sub1.children" :key="sub2.id" class="tree-item">
                      <div class="tree-row">
                        <span class="expand-icon empty"></span>
                        <a href="#" @click.prevent="filterByCategory(sub2.id)" 
                           :class="{ active: activeCategoryId === sub2.id }">
                          {{ sub2.name }} ({{ sub2.count }})
                        </a>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </aside>

      <div class="main-content">
        <div class="article-list">
          <template v-for="article in articles">
            <div v-if="article.external_link" :key="'ext-'+article.id" class="article-item" @click="handleExternalLink(article)">
              <div class="article-image-wrapper">
                <img :src="article.image" :alt="article.title" />
              </div>
              <div class="article-info">
                <h3 class="article-title">{{ article.title }}</h3>
                <p class="article-summary">{{ article.summary }}</p>
                <span class="article-date">{{ article.date }}</span>
              </div>
            </div>
            <div v-else :key="'int-'+article.id" class="article-item" @click="goToDetail(article)">
              <div class="article-image-wrapper">
                <img :src="article.image" :alt="article.title" />
              </div>
              <div class="article-info">
                <h3 class="article-title">{{ article.title }}</h3>
                <p class="article-summary">{{ article.summary }}</p>
                <span class="article-date">{{ article.date }}</span>
              </div>
            </div>
          </template>
        </div>

        <div class="pagination-wrapper">
          <span class="total-text">共 {{ totalItems }} 条</span>
          <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
          
          <template v-if="totalPages <= 7">
            <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
              {{ page }}
            </button>
          </template>
          
          <template v-else>
            <template v-if="currentPage <= 4">
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
            
            <template v-else-if="currentPage >= totalPages - 3">
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
            </template>
            
            <template v-else>
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages.slice(2, 5)" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
          </template>
          
          <button class="page-btn" :disabled="currentPage >= totalPages" @click="nextPage">&gt;</button>
          <select class="page-size-select" v-model="pageSize" @change="onPageSizeChange">
            <option value="10">10 条/页</option>
            <option value="20">20 条/页</option>
            <option value="30">30 条/页</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ImageNews',
  data() {
    return {
      articles: [],
      categories: [],
      flatCategories: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalItems: 0,
      activeCategoryId: 2,
      expandedCats: [],
      defaultRootCat: { id: 2, name: '新闻中心' },
      searchKeyword: ''
    }
  },
  computed: {
    currentCategoryName() {
      const cat = this.flatCategories.find(c => c.id === this.activeCategoryId)
      if (cat && cat.name) {
        return cat.name
      }
      return this.defaultRootCat.name
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
        } else if (current >= total - 3) {
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push(current - 1, current, current + 1)
          pages.push(total)
        }
      }
      return pages
    }
  },
  mounted() {
    this.fetchCategories().then(() => {
      this.fetchArticles()
    })
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await axios.get('/api/categories')
        if (response.data.success) {
          this.categories = response.data.data
          this.flatCategories = Array.isArray(response.data.flat) ? response.data.flat : []
        }
        if (this.flatCategories.length === 0) {
          this.flatCategories = this.extractFlatCategories(this.categories)
        }
        // 查找"图片新闻"分类并高亮
        this.findAndSelectImageNewsCategory()
      } catch (error) {
        console.error('获取分类列表失败:', error)
        this.categories = []
        this.flatCategories = []
      }
    },
    extractFlatCategories(cats, parentId = 0) {
      const result = []
      const arr = Array.isArray(cats) ? cats : []
      arr.forEach(cat => {
        if (cat) {
          result.push({
            id: (cat.id !== undefined && cat.id !== null) ? cat.id : (cat.ID !== undefined && cat.ID !== null ? cat.ID : 0),
            name: (cat.name || '').toString(),
            parent: parentId
          })
          if (cat.children && Array.isArray(cat.children)) {
            result.push(...this.extractFlatCategories(cat.children, cat.id || 0))
          }
        }
      })
      return result
    },
    findAndSelectImageNewsCategory() {
      const cat = this.flatCategories.find(c => c.name === '图片新闻')
      if (cat) {
        this.activeCategoryId = cat.id
      }
    },
    async fetchArticles() {
      try {
        const response = await axios.get('/api/posts', {
          params: {
            page: this.currentPage,
            size: this.pageSize,
            category_id: this.activeCategoryId
          }
        })
        if (response.data.success) {
          this.articles = response.data.data
          this.totalItems = response.data.total || this.articles.length
          this.totalPages = Math.ceil(this.totalItems / this.pageSize)
        }
      } catch (error) {
        console.error('获取文章列表失败:', error)
        this.articles = []
      }
    },
    toggleExpand(catId) {
      const index = this.expandedCats.indexOf(catId)
      if (index > -1) {
        this.expandedCats.splice(index, 1)
      } else {
        this.expandedCats.push(catId)
      }
    },
    filterByCategory(catId) {
      this.activeCategoryId = catId
      this.currentPage = 1
      this.fetchArticles()
    },
    async goToDetail(article) {
      if (typeof article === 'object' && article.external_link) {
        await this.handleExternalLink(article)
      } else {
        const id = typeof article === 'object' ? article.id : article
        this.$router.push(`/article/${id}`)
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchArticles()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchArticles()
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page
        this.fetchArticles()
      }
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchArticles()
    },
    async handleExternalLink(article) {
      try {
        const response = await axios.get(`/api/hit/${article.id}`)
        console.log('外链浏览记录写入成功:', response.data)
        const link = response.data.external_link || article.external_link
        window.open(link, '_blank')
      } catch (err) {
        console.error('记录外链浏览失败，直接跳转:', err)
        window.open(article.external_link, '_blank')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.image-news-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.image-news-page .page-header {
  position: sticky;
  top: 198px;
  z-index: 150;
  background: white;
  border-bottom: 1px solid #eee;
  padding: 15px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    width: 100%;
  }

  .header-left {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-left: 20px;

    a {
      color: #4ecdc4;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    span {
      margin: 0 8px;
      color: #999;
    }
  }

  .current-category-title {
    font-size: 24px;
    font-weight: bold;
    color: #4ecdc4;
    margin: 0;
  }
}

.image-news-page .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
  display: flex;
  gap: 30px;
}

.image-news-page .sidebar {
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 175px;
  align-self: flex-start;
}

.image-news-page .sidebar-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #4ecdc4;
  }
}

.image-news-page .category-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li.tree-item {
    border-bottom: 1px dashed #eee;

    &:last-child {
      border-bottom: none;
    }

    .tree-row {
      display: flex;
      align-items: center;
      padding: 6px 0;

      .expand-icon {
        cursor: pointer;
        font-size: 8px;
        width: 12px;
        height: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 6px;
        color: #4ecdc4;

        &.empty {
          cursor: default;
          color: transparent;
        }
      }

      a {
        flex: 1;
        font-size: 14px;
        color: #666;
        text-decoration: none;

        &:hover, &.active {
          color: #4ecdc4;
          font-weight: bold;
        }
      }
    }

    .sub-category {
      list-style: none;
      padding: 0 0 0 20px;
      margin: 0;

      li.tree-item {
        border-bottom: none;
      }
    }
  }
}

.image-news-page .main-content {
  flex: 1;
  max-width: calc(100% - 330px);
  box-sizing: border-box;
}

.image-news-page .article-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.image-news-page .article-item {
  display: flex;
  gap: 20px;
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  }

  .article-image-wrapper {
    width: 200px;
    height: 150px;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  .article-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .article-title {
        font-size: 20px;
        font-weight: bold;
        color: #333;
        margin: 0 0 10px 0;
        line-height: 1.4;
        transition: color 0.2s;
      }

    &:hover .article-title {
      color: #4ecdc4;
    }

    .article-summary {
      font-size: 14px;
      color: #666;
      line-height: 1.6;
      margin: 0 0 10px 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .article-date {
      font-size: 14px;
      color: #999;
    }
  }
}

.image-news-page .pagination-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  .total-text {
    font-size: 14px;
    color: #333;
    margin-right: 10px;
    line-height: 1.4;
  }

  .page-btn {
    width: 38px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #eee;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &.active {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .ellipsis {
    font-size: 14px;
    color: #999;
    width: 24px;
    text-align: center;
  }

  .page-size-select {
    width: 100px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    padding: 0 30px 0 10px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    transition: border-color 0.3s;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%234ecdc4' d='M6 8L0 2L1.4 0.6L6 5.2L10.6 0.6L12 2L6 8Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    
    &:focus {
      border-color: #4ecdc4;
    }
  }
}

@media (max-width: 768px) {
  .image-news-page .article-item {
    flex-direction: column;

    .article-image-wrapper {
      width: 100%;
      height: 200px;
    }

    .article-info {
      .article-title {
        font-size: 18px;
      }
    }
  }
}
</style>